import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { Deployment } from 'src/app/core/models/deployment';
import { AuthType } from 'src/app/core/models/globals';
import { addUserRoles } from 'src/app/core/models/user';
import { UserRoleNames, UserRolesInt, Users } from 'src/app/core/models/users';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { DeploymentService } from 'src/app/core/services/deployment.service';
import { NodeService } from 'src/app/core/services/node.service';
import { UserService } from 'src/app/core/services/user.service';

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss']
})
export class AddUserComponent implements OnInit {
  fieldTextType: boolean = true;
  addUserForm: UntypedFormGroup;
  deployments: Deployment[];
  maxAllowedUser: number= this.data.maxAllowedUser;
  totalUsersAdded: number = this.data.addedUsers;
  addUserLimitReached: boolean = false;
  addUserToIdentityServer: boolean = this.data.addUserToIdentityServer;
  isHeadNodeActive: boolean = false;
  globalAdminFlag: boolean = false;
  deploymentNames: string[] = [];
  showJobSubmitSessionIdField: boolean = false;
  showUidGidFields: boolean = false;
  hpcDeploymentSelected: boolean = false;
  vdiDeploymentSelected: boolean = false;
  private userIdSubscription!: Subscription;
  hpcDeployments: string[] = [];
  vdiDeployments: string[] = [];
  showJobSubmitId: boolean = false;
  showSessionId: boolean = false;
  authType: number = this.authenticationService.getAuthenticationType() || AuthType.SyncHPCAPI;

  constructor(
    public dialog: MatDialogRef<AddUserComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      addedUsers: number,
      maxAllowedUser: number,
      addUserToIdentityServer: boolean 
    },
    private depService: DeploymentService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private snackBar: MatSnackBar,
    private nodeService: NodeService,
    private spinner: NgxSpinnerService,
  ) {}

  types: addUserRoles[] = [
    { name: UserRoleNames.GlobalAdmin, value: UserRolesInt.GlobalAdmin },
    { name: UserRoleNames.DeploymentAdmin, value: UserRolesInt.DeploymentAdmin },
    // { name: UserRoleNames.PowerUser, value: UserRolesInt.PowerUser },
    { name: UserRoleNames.JobOperator, value: UserRolesInt.JobOperator },
    { name: UserRoleNames.Reader, value: UserRolesInt.Reader },
  ];
  confirmPasswordValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!this.addUserForm) {
      return null;
    }

    const password = this.addUserForm.get('addUserPassword')?.value;
    const confirmPassword = control.value;

    if (!password || !confirmPassword) {
      return null;
    }

    return password === confirmPassword ? null : { passwordMismatch: true };
  };

  ngOnInit() {

    if(this.authType == AuthType.SyncHPCAPI && this.addUserToIdentityServer === false){
      this.snackBar.open("If Authentication type is SyncHPC API, user has to be added to Identity server, if not already added. Please check 'Add User To Identity Server' flag in settings.", "OK", { duration: 10000 });
    }

    this.depService.getAllDeploymentsForUser()
      .subscribe((data) => {
        this.deployments = <Deployment[]>JSON.parse(JSON.stringify(data));
        this.deployments = this.deployments.filter(x => x.status == 'Active');
        if (this.deployments) {
          this.deploymentNames = this.deployments?.map(dp => dp?.name)
          this.hpcDeployments = this.deployments?.filter(dp => dp.isVdi == false)?.map(dp => dp.name);
          this.vdiDeployments = this.deployments?.filter(dp => dp.isVdi === true)?.map(dp => dp.name);
        }
      });

    if(this.totalUsersAdded >= this.maxAllowedUser + 1 ){
      this.addUserLimitReached = true
    }

    let userRole = this.authenticationService.currentUserValue.role;
    if (userRole != UserRoleNames.GlobalAdmin) {
      this.types = this.types.filter(x => x.value != UserRolesInt.GlobalAdmin && x.value != UserRolesInt.Reader);
    }
    
    this.addUserForm = new UntypedFormGroup({
      addUserFirstName: new UntypedFormControl(''),
      addUserLastName: new UntypedFormControl(''),
      addUserEmail: new UntypedFormControl(''),
      addUserUserId: new UntypedFormControl(''),
      addUserUserRole: new UntypedFormControl('', Validators.required),
      addUserDeployment: new UntypedFormControl(''),
      addUserPassword: new UntypedFormControl(''),
      addUserConfirmPassword: new UntypedFormControl('', [Validators.required, this.confirmPasswordValidator]),
      addUserPublicKey: new UntypedFormControl(''),
      addUserCommand: new UntypedFormControl(''),
      addUserAdditionalInfo: new UntypedFormControl(''),
      addUserCustomUid: new UntypedFormControl(''),
      addUserCustomGid: new UntypedFormControl(''),
    });

    if(this.addUserToIdentityServer === true){
      this.addUserForm.controls['addUserPassword'].setValidators([Validators.required]);
      this.addUserForm.controls['addUserPassword'].updateValueAndValidity();
      this.addUserForm.controls['addUserUserId'].setValidators([Validators.required, Validators.pattern('^[a-zA-Z0-9][a-zA-Z0-9._-]{0,31}$')]);
      this.addUserForm.controls['addUserUserId'].updateValueAndValidity();
    }
    else {
      this.addUserForm.controls['addUserPassword'].disable();
      this.addUserForm.controls['addUserConfirmPassword'].disable();
    }

    this.addUserForm.controls['addUserDeployment'].valueChanges.subscribe(val => {
      this.checkIfHeadNodeActive(val);
    })
    this.addUserForm.get('addUserPassword')?.valueChanges.subscribe(() => {
      this.addUserForm.get('addUserConfirmPassword')?.updateValueAndValidity();
    });
    this.addUserForm.controls['addUserUserRole'].markAsTouched();
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    // if (this.snackBar) this.snackBar.dismiss();
    if (this.userIdSubscription) {
      this.userIdSubscription.unsubscribe();
    }
  }

  showError(form) {
    this.addUserForm.markAllAsTouched();
  }

  onClose() {
    this.dialog.close(false);
  }

  togglePassswordIcon() {
    this.fieldTextType = !this.fieldTextType
  }

  onSubmit(form) {
    try {
      let deployments = form.value.addUserDeployment;
      if (!form.valid) {
        this.showError(form);
      } else {
        let depString = null
        if (!this.globalAdminFlag && form.value?.addUserUserRole != "4") {
          if (deployments.includes("None") && deployments.length > 1) {
            this.snackBar.open("Please select None or Deployments. Can't Select None and deployments", "OK", { duration: 5000 });
            return
          } else if (deployments.includes("None") && deployments.length === 1) {
            depString = null
          } else {
            depString = form.value.addUserDeployment.toString();
          }
        }
        if (form.value.addUserPassword == form.value.addUserConfirmPassword) {
          let user: Users = {
            adminId: this.authenticationService.currentUserValue.username,
            firstName: form.value.addUserFirstName.trim(),
            lastName: form.value.addUserLastName.trim(),
            userName: form.value.addUserUserId,
            emailId: form.value.addUserEmail,
            userRole: form.value.addUserUserRole,
            depName: depString,
            passwd: form.value.addUserConfirmPassword,
            publicKey: form.value.addUserPublicKey,
            userStartCommand: form.value.addUserCommand,
            additionalInfo: form.value.addUserAdditionalInfo,
            addUserCustomUid: form.value.addUserCustomUid,
            addUserCustomGid: form.value.addUserCustomGid,
          };
          this.spinner.show("addUser");
          this.userService.addUser(user).subscribe({
            next: (data: any) => {
              this.snackBar.open(data.message, "Ok", { duration: 5000 });
              this.spinner.hide("addUser");
              this.dialog.close(data?.message);
            },
            error: (err: any) => {
              this.snackBar.open(
                err.error.message || "Cannot add user.",
                "OK",
                { duration: 5000 }
              );
              this.spinner.hide("addUser");
              this.dialog.close(false);
            },
          });
        } else {
          this.spinner.hide("addUser");
          this.snackBar.open("Password and Confirm Password does not match", "OK", { duration: 5000 });
          return;
        }
      }
    }
    catch (error) {
      this.spinner.hide("addUser");
      this.snackBar.open("Error occurred while adding user", "OK", { duration: 5000 });
      console.error("Error occurred while adding user:", error);
      this.dialog.close(false);
    }
  }

  checkIfHeadNodeActive(depName: string[]) {
    try {
      this.spinner.show("addUser")
      let deploymentsSelected = [...depName];
      if (depName != null && depName?.length === 0) {
        this.addUserForm.controls['addUserUserRole'].enable();
        this.isHeadNodeActive = true;
        this.spinner.hide("addUser");
        return;
      }
      if (depName.includes("None") && depName.length === 1) {
        this.addUserForm.controls['addUserUserRole'].enable();
        this.isHeadNodeActive = true;
        this.spinner.hide("addUser");
        return;
      }

      else if (depName.includes("None") && depName.length > 1) {
        this.addUserForm.controls['addUserUserRole'].enable();
        let index = depName.indexOf("None");
        if (index > -1) {
          deploymentsSelected?.splice(index, 1);
        }
      }
      else if (!depName.includes("None") && depName.length > 0) {
        this.addUserForm.controls['addUserUserRole'].enable();
      }

      deploymentsSelected?.forEach(dep => {
        this.nodeService.checkIsHeadNodeActive(dep).subscribe({
          next: (data: any) => {
            this.isHeadNodeActive = data.isHeadNodeActive;
            if (this.isHeadNodeActive == false) {
              this.snackBar.open("All Nodes are not active for " + dep + ", new user cannot be added.", "Ok")
              this.spinner.hide("addUser")
              return;
            }
            else {
              this.spinner.hide("addUser")
              this.snackBar.dismiss();
            }
          }
        })
      })
    }
    catch (error) {
      this.spinner.hide("addUser");
    }
  }

  optionClicked(value: string) {
    if (value == "0" || value == "4") {
      this.addUserForm.controls['addUserDeployment'].disable();
      this.globalAdminFlag = true;
      this.checkIfHeadNodeActive(this.deploymentNames)
    }
    else {
      this.addUserForm.controls['addUserDeployment'].enable();
      this.globalAdminFlag = false;
    }
  }

  shouldShowCustomUidGid(): boolean {
    this.showUidGidFields = (this.addUserForm.get('addUserToIdentityServer')?.value ||
      this.addUserForm.get('addUserToCluster')?.value) && this.addUserForm.get('addUserUseCustomUidGid')?.value;
    return !!(
      this.addUserForm.get('addUserToIdentityServer')?.value ||
      this.addUserForm.get('addUserToCluster')?.value
    );
  }
}
